import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  Save, ArrowLeft, Loader2, ShieldCheck, CheckCircle2, XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ALL_MODULES } from '../utils/organizationModules';
import { MODULE_ORG_ALIAS_MAP } from '../utils/accessModules';
import { buildUserModuleOverrides } from '../utils/userModulePermissions';

const ROLES = ['cajero', 'vendedor', 'operario', 'encargado', 'admin', 'administrador'];
const CATEGORY_ORDER = ['Principal', 'Ventas', 'CRM', 'Community Manager', 'Digital', 'Logistica', 'Compras', 'Inventario', 'Produccion', 'Finanzas', 'Sistema', 'Configuracion'];
const DEFAULT_PASSWORD = '123456';

function UsuarioNuevo({ onBack, onNavigate }) {
  const { profile } = useAuth();
  const canManageUsers = ['admin', 'administrador'].includes(String(profile?.role || '').toLowerCase());

  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({ email: '', full_name: '', role: 'cajero' });
  const [selectedPerms, setSelectedPerms] = useState(new Set(['dashboard']));
  const [baseRolePerms, setBaseRolePerms] = useState([]);
  const [orgModules, setOrgModules] = useState(null);

  useEffect(() => {
    const fetchContext = async () => {
      if (!profile?.organization_id) return;

      const [{ data: roleData }, { data: orgData, error: orgError }] = await Promise.all([
        supabase
          .from('role_templates')
          .select('module_id')
          .eq('role_name', formData.role)
          .eq('organization_id', profile.organization_id),
        supabase
          .from('organizations')
          .select('custom_modules')
          .eq('id', profile.organization_id)
          .single(),
      ]);

      if (orgError) {
        console.error(orgError);
        toast.error('No pudimos cargar los modulos de la organizacion');
        return;
      }

      const baseModules = (roleData || []).map((row) => row.module_id).filter(Boolean);
      setBaseRolePerms(baseModules);
      setSelectedPerms(new Set(baseModules.length ? baseModules : ['dashboard']));
      setOrgModules(Array.isArray(orgData?.custom_modules) ? orgData.custom_modules : null);
    };

    fetchContext();
  }, [formData.role, profile?.organization_id]);

  const groupedModules = useMemo(() => {
    const orgModulesSet = Array.isArray(orgModules) ? new Set(orgModules) : null;

    const filtered = ALL_MODULES.filter((moduleRow) => {
      if (!orgModulesSet) return true;
      const candidateIds = [moduleRow.id, ...(MODULE_ORG_ALIAS_MAP[moduleRow.id] || [])];
      return candidateIds.some((candidateId) => orgModulesSet.has(candidateId));
    });

    const categoryMap = filtered.reduce((accumulator, moduleRow) => {
      const group = moduleRow.category || 'Otros';
      if (!accumulator[group]) accumulator[group] = [];
      accumulator[group].push(moduleRow);
      return accumulator;
    }, {});

    return Object.entries(categoryMap)
      .sort((left, right) => {
        const leftIndex = CATEGORY_ORDER.indexOf(left[0]);
        const rightIndex = CATEGORY_ORDER.indexOf(right[0]);
        const safeLeft = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
        const safeRight = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;
        if (safeLeft !== safeRight) return safeLeft - safeRight;
        return left[0].localeCompare(right[0]);
      })
      .map(([group, items]) => ({
        group,
        items: [...items].sort((left, right) => left.label.localeCompare(right.label)),
      }));
  }, [orgModules]);

  const togglePermiso = (moduleId) => {
    if (!canManageUsers) return;

    setSelectedPerms((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) next.delete(moduleId);
      else next.add(moduleId);
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!profile?.organization_id) return toast.error('Sesion no detectada');
    if (!canManageUsers) return toast.error('Solo un admin puede crear usuarios');

    setIsSaving(true);
    try {
      const orgId = profile.organization_id;

      const emailNormalized = formData.email.toLowerCase();

      const { data: existingUser } = await supabase
        .from('profiles')
        .select('email')
        .eq('email', emailNormalized)
        .maybeSingle();

      if (existingUser) {
        setIsSaving(false);
        return toast.error('Este correo ya esta registrado en el sistema');
      }

      const { data: createResult, error: createError } = await supabase.functions.invoke('create-user', {
        body: {
          email: emailNormalized,
          password: DEFAULT_PASSWORD,
          full_name: formData.full_name.toUpperCase(),
          role: formData.role,
          organization_id: orgId,
        },
      });

      if (createError || createResult?.error) {
        throw new Error(createResult?.error || createError?.message || 'No se pudo crear el usuario');
      }

      const newUserId = createResult?.user_id;
      if (!newUserId) throw new Error('La funcion no devolvio el ID del nuevo usuario');

      const overrides = buildUserModuleOverrides({
        baseModuleIds: baseRolePerms,
        selectedModuleIds: [...selectedPerms],
      });

      if (overrides.length > 0) {
        const rows = overrides.map((row) => ({
          organization_id: orgId,
          user_id: newUserId,
          role_name: formData.role,
          module_id: row.module_id,
          is_allowed: row.is_allowed,
        }));

        const { error: permsError } = await supabase.from('role_permissions').insert(rows);
        if (permsError) throw permsError;
      }

      toast.success(`Usuario creado. Contrasena inicial: ${DEFAULT_PASSWORD}`, { duration: 6000 });
      if (onNavigate) return onNavigate('permisos');
      if (onBack) return onBack();
    } catch (err) {
      console.error(err);
      toast.error('Error al registrar el miembro');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 bg-[#F2F9FF] min-h-screen font-sans animate-nova-in">
      <div className="flex items-center gap-4 mb-8">
        <button type="button" onClick={() => onNavigate ? onNavigate('permisos') : onBack && onBack()} className="p-3 bg-white border border-slate-200 rounded-2xl text-slate-400 hover:text-[#3B82F6] shadow-sm transition-all">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-slate-800 tracking-tight italic uppercase mt-1">Alta de Miembro</h1>
      </div>

      {!canManageUsers ? (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-[11px] font-bold text-amber-800">
          Solo un administrador puede crear usuarios y definir modulos visibles.
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm p-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Nombre Completo</label>
              <input data-onboarding-id="team-name-input" required value={formData.full_name} disabled={!canManageUsers} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-4 py-4 text-sm font-bold outline-none disabled:opacity-60" placeholder="EJ: MARIA GARCIA" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Email Laboral</label>
              <input data-onboarding-id="team-email-input" required value={formData.email} disabled={!canManageUsers} onChange={(e) => setFormData({ ...formData, email: e.target.value })} type="email" className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-4 py-4 text-sm font-bold outline-none disabled:opacity-60" placeholder="EMAIL@EMPRESA.COM" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Rol Asignado</label>
              <select data-onboarding-id="team-role-select" value={formData.role} disabled={!canManageUsers} onChange={(e) => setFormData({ ...formData, role: e.target.value })} className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-4 py-4 text-sm font-bold outline-none appearance-none cursor-pointer disabled:opacity-60">
                {ROLES.map((roleName) => <option key={roleName} value={roleName}>{roleName.toUpperCase()}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm p-10">
          <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2 italic"><ShieldCheck size={14} /> Modulos visibles para este usuario</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {groupedModules.map((group) => (
              <div key={group.group} className="space-y-4">
                <p className="text-[11px] font-black text-[#1b2559] uppercase border-b border-slate-50 pb-2 italic">{group.group}</p>
                {group.items.map((item) => (
                  <button key={item.id} type="button" onClick={() => togglePermiso(item.id)} disabled={!canManageUsers} className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                    <span className="text-xs font-bold text-slate-600 uppercase tracking-tight text-left">{item.label}</span>
                    <div className={selectedPerms.has(item.id) ? 'text-[#3FF2AA]' : 'text-slate-200'}>
                      {selectedPerms.has(item.id) ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <button data-onboarding-id="team-submit-btn" disabled={isSaving || !canManageUsers} type="submit" className="bg-[#3B82F6] text-white px-12 py-5 rounded-[2rem] font-bold uppercase text-[10px] tracking-widest shadow-xl hover:bg-blue-600 transition-all flex items-center gap-3 disabled:opacity-60">
            {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Finalizar Registro
          </button>
        </div>
      </form>
    </div>
  );
}

export default UsuarioNuevo;
